import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, X, ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl, formatIDR } from "@/lib/api";

const CONDITIONS = ["Seperti Baru", "Sangat Baik", "Baik", "Cukup"];
const LOCATIONS = ["Jakarta", "Bandung", "Surabaya", "Yogyakarta", "Medan", "Semarang", "Bali"];

export default function Sell() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", category: "fashion", condition: "Sangat Baik", price: "", location: "Jakarta",
  });

  useEffect(() => {
    api.get("/categories").then((r) => {
      setCategories(r.data);
      setForm((f) => ({ ...f, category: r.data[0]?.slug || "fashion" }));
    });
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((prev) => [...prev, data.path]);
      }
      toast.success("Foto diunggah");
    } catch {
      toast.error("Gagal mengunggah foto");
    } finally {
      setUploading(false);
    }
  };

  const removeImg = (i) => setImages((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!images.length) return toast.error("Tambahkan minimal 1 foto");
    if (!form.title.trim()) return toast.error("Judul wajib diisi");
    if (!form.price || Number(form.price) <= 0) return toast.error("Harga tidak valid");
    setSubmitting(true);
    try {
      const { data } = await api.post("/products", {
        ...form,
        price: Number(form.price),
        images,
      });
      toast.success("Produk berhasil diterbitkan!");
      navigate(`/product/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menerbitkan");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-2xl font-extrabold text-gray-900 mb-1">Jual Barang Preloved</h1>
      <p className="text-sm text-gray-500 mb-6">Foto jelas + deskripsi jujur = cepat laku ✨</p>

      <form onSubmit={submit} className="space-y-5">
        {/* Images */}
        <div>
          <label className="text-sm font-semibold text-gray-800">Foto Produk</label>
          <div className="mt-2 grid grid-cols-4 sm:grid-cols-5 gap-2">
            {images.map((im, i) => (
              <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-gray-200">
                <img src={fileUrl(im)} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removeImg(i)} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            <label data-testid="upload-image-button" className="aspect-square rounded-xl border-2 border-dashed border-[#DBEAFE] flex flex-col items-center justify-center text-[#0B4DA2] cursor-pointer bg-[#EFF6FF]">
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}
              <span className="text-[10px] mt-1">Tambah</span>
              <input type="file" accept="image/*" multiple className="hidden" onChange={onUpload} />
            </label>
          </div>
        </div>

        <Field label="Judul">
          <input data-testid="sell-title-input" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Cth: Jaket Denim Vintage Size M" className="input" />
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Kategori">
            <select data-testid="sell-category-select" value={form.category} onChange={(e) => set("category", e.target.value)} className="input">
              {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Kondisi">
            <select data-testid="sell-condition-select" value={form.condition} onChange={(e) => set("condition", e.target.value)} className="input">
              {CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Harga (Rp)">
            <input data-testid="sell-price-input" type="number" value={form.price} onChange={(e) => set("price", e.target.value)} placeholder="150000" className="input" />
          </Field>
          <Field label="Lokasi">
            <select data-testid="sell-location-select" value={form.location} onChange={(e) => set("location", e.target.value)} className="input">
              {LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </Field>
        </div>

        <Field label="Deskripsi">
          <textarea data-testid="sell-description-input" value={form.description} onChange={(e) => set("description", e.target.value)} rows={4} placeholder="Ceritakan kondisi, kelengkapan, alasan jual..." className="input resize-none" />
        </Field>

        {form.price > 0 && <div className="text-sm text-gray-500">Pembeli membayar: <b className="text-[#0B4DA2]">{formatIDR(Number(form.price) + 2500)}</b> (termasuk biaya admin Rp 2.500)</div>}

        <button data-testid="publish-product-button" disabled={submitting} className="w-full py-3.5 rounded-xl bg-[#0B4DA2] hover:bg-[#083B7C] text-white font-semibold transition disabled:opacity-60 flex items-center justify-center gap-2">
          {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
          Terbitkan Produk
        </button>
      </form>

      <style>{`.input{width:100%;padding:0.75rem 1rem;border-radius:0.75rem;border:1px solid #E5E7EB;outline:none;font-size:0.875rem;background:#fff}.input:focus{border-color:#0B4DA2}`}</style>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-sm font-semibold text-gray-800 block mb-1.5">{label}</label>
    {children}
  </div>
);
